import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ActionMenu,
  Button,
  Card,
  CardBody,
  Input,
  Pagination,
  StatusPill,
  Switch,
  TableScroll,
  useCursorPagination,
} from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import { Plus, Search } from "lucide-react";
import { useAuthStore } from "../../auth/auth-store.js";
import { getTeamUsage } from "../../api/identity-admin-api.js";
import { listAdminTeams } from "../../api/teams-api.js";
import { TEAMS_ROUTE } from "../../routes.js";

const PAGE_SIZE = 25;

export function IdentityTeamsTab(): ReactElement {
  const navigate = useNavigate();
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? "none");
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const paging = useCursorPagination(
    `${tenantId}|${normalizedQuery}|${showArchived}`,
    { pageSize: PAGE_SIZE },
  );
  const teams = useQuery({
    queryKey: [
      "identity-admin-teams",
      tenantId,
      normalizedQuery,
      showArchived,
      paging.cursor ?? "first",
    ],
    queryFn: () =>
      listAdminTeams({
        query: normalizedQuery,
        includeArchived: showArchived,
        ...(paging.cursor ? { cursor: paging.cursor } : {}),
        limit: PAGE_SIZE,
      }),
  });
  paging.reportTotal(teams.data?.totalCount);
  const rows = teams.data?.items ?? [];
  const usage = useQuery({
    queryKey: ["identity-team-usage", tenantId, rows.map((row) => row.teamId)],
    queryFn: () => getTeamUsage(rows.map((row) => row.teamId)),
    enabled: rows.length > 0,
    /* NO RETRY, and this is what makes the unknown marker actually reachable
       (PB-5332, found by driving the page against a stopped LAMA service).
       `isError` was correct in the component and provably correct in the unit
       spec, but in the browser the column sat on the loading marker
       INDEFINITELY: `getTeamUsage` rejects in ~25ms with the upstream's 502,
       and the query then stayed `status: "pending" / fetchStatus: "fetching"`
       under the default retry policy rather than settling into the error the
       cell branches on. A decorative count is the wrong place to spend
       retries anyway — the page has three other panels to render, and "we
       could not read this" is a perfectly good answer to give immediately. */
    retry: false,
  });
  const usageByTeam = new Map(
    (usage.data ?? []).map((item) => [item.teamId, item.campaignCount]),
  );
  /* PB-5332 review B3. What this column may ASSERT, separated from what it
     renders. It used to read `usageByTeam.get(id) ?? 0`, so any failure of the
     usage query — which settles with `data === undefined` — printed `0` under a
     header saying "Campaigns" on every row, stating a fact the page did not
     have.

     That is not a rare path. LAMA is enabled per deployment via `PB_MODULES`
     and the route carries `@RequiresModule("lama")`, so wherever LAMA is off
     the column permanently claimed zero; and since the route is now correctly
     gated on `transformation-lead` (B2), a Tenant Admin without that role is
     refused and gets the same reading.

     It matters because the row's own menu offers Archive team, and "used by no
     campaigns" is exactly the signal an administrator would archive on. An
     unknown must look unknown. */
  const UNKNOWN_USAGE = "—";
  const UNKNOWN_USAGE_REASON = "Campaign usage is unavailable.";
  function campaignsCell(teamId: string): string | number {
    if (usage.isPending) return "…";
    if (usage.isError) return UNKNOWN_USAGE;
    /* Present in a SUCCESSFUL response but absent from it means the server
       returned no row for this team, which is a real zero. Absent because the
       response never arrived is handled above. */
    return usageByTeam.get(teamId) ?? 0;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-pb-text">Shared teams</h2>
          <p className="mt-1 text-sm text-pb-text-secondary">
            Accepted member collections used across {PRODUCT_NAME}. Team
            membership never grants product access.
          </p>
        </div>
        <Button onClick={() => navigate(`${TEAMS_ROUTE}?action=create`)}>
          <Plus className="h-4 w-4" aria-hidden /> Create team
        </Button>
      </div>

      <Card>
        <CardBody flush>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-pb-border p-4">
            <Input
              aria-label="Search teams"
              placeholder="Search teams"
              value={query}
              prefix={<Search className="h-4 w-4" aria-hidden />}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Switch
              checked={showArchived}
              onChange={setShowArchived}
              label="Show archived"
            />
          </div>

          {teams.isPending ? (
            <p role="status" className="p-6 text-sm text-pb-text-muted">
              Loading teams…
            </p>
          ) : null}
          {teams.error ? (
            <p role="alert" className="p-6 text-sm text-pb-red-text">
              {teams.error.message}
            </p>
          ) : null}
          {teams.isSuccess && rows.length === 0 ? (
            <p className="p-6 text-sm text-pb-text-muted">
              No teams match these filters.
            </p>
          ) : null}
          {rows.length > 0 ? (
            <TableScroll aria-label="Identity teams" embedded>
              {/* eslint-disable-next-line ds/no-hand-built -- DataTable would restyle this header (uppercase 10px labels) and cell padding; no PR 3 look row approves that, so the raw table stays inside TableScroll. */}
              <table className="w-full text-dense-1">
                <thead className="bg-pb-card-elevated text-left">
                  <tr className="border-b border-pb-border">
                    <th className="px-4 py-3 font-medium">Team</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Members</th>
                    <th className="px-4 py-3 font-medium">Campaigns</th>
                    <th className="px-4 py-3 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((team) => {
                    const isOwner = team.viewerRole === "owner";
                    /* An archived team takes no owner change — the server
                       refuses it with this sentence — so its reason wins over
                       the owner-only one (d8). */
                    const ownerChange = (ownerOnlyReason: string) =>
                      team.archivedAt
                        ? { disabledReason: "This team is archived." }
                        : !isOwner
                          ? { disabledReason: ownerOnlyReason }
                          : {};
                    return (
                      <tr
                        key={team.teamId}
                        className="border-b border-pb-border last:border-0"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-pb-text">
                            {team.name}
                          </div>
                          <div className="mt-0.5 max-w-xl truncate text-pb-text-muted">
                            {team.description || "No description"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill
                            tone={team.archivedAt ? "neutral" : "healthy"}
                          >
                            {team.archivedAt ? "Archived" : "Active"}
                          </StatusPill>
                        </td>
                        <td className="px-4 py-3 text-pb-text-secondary">
                          {team.memberCount} accepted
                        </td>
                        {/* The marker explains itself rather than being a bare
                            dash: this column can be unknown for the whole
                            session (LAMA off for the deployment, or the viewer
                            lacking `transformation-lead`), so a silent "—"
                            leaves an administrator guessing whether it means
                            zero.

                            Carried by VISUALLY-HIDDEN TEXT, not a `title`
                            attribute (PB-5332 re-review). A title is announced
                            inconsistently across screen readers and never on
                            touch, so the one reading that must not be mistaken
                            for zero was the one least reliably conveyed. The
                            `title` is kept alongside for the sighted hover
                            affordance it does provide. */}
                        <td
                          className="px-4 py-3 text-pb-text-secondary"
                          {...(usage.isError
                            ? { title: UNKNOWN_USAGE_REASON }
                            : {})}
                        >
                          {campaignsCell(team.teamId)}
                          {usage.isError ? (
                            <span className="sr-only">
                              {` ${UNKNOWN_USAGE_REASON}`}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <ActionMenu
                            triggerSize="sm"
                            triggerLabel={`Actions for ${team.name}`}
                            items={[
                              {
                                id: "open",
                                label: "Open team",
                                onSelect: () =>
                                  navigate(`${TEAMS_ROUTE}/${team.teamId}`),
                              },
                              {
                                id: "rename",
                                label: "Rename",
                                ...ownerChange(
                                  "Only a team owner can rename this team.",
                                ),
                                // The team page opens its Edit details dialog.
                                onSelect: () =>
                                  navigate(
                                    `${TEAMS_ROUTE}/${team.teamId}?action=edit`,
                                  ),
                              },
                              {
                                id: "members",
                                label: "Manage members",
                                ...ownerChange(
                                  "Only a team owner can manage members.",
                                ),
                                onSelect: () =>
                                  navigate(`${TEAMS_ROUTE}/${team.teamId}`),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          ) : null}
          {paging.page > 1 || teams.data?.nextCursor ? (
            <div className="flex justify-end border-t border-pb-border p-3">
              <Pagination
                mode="cursor"
                page={paging.page}
                pageItemCount={rows.length}
                totalCount={paging.totalCount}
                canGoBack={paging.canGoBack}
                hasNext={Boolean(teams.data?.nextCursor)}
                busy={teams.isFetching}
                onPrev={paging.goBack}
                onNext={() => {
                  const cursor = teams.data?.nextCursor;
                  if (cursor) paging.goNext(cursor);
                }}
              />
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
